import Folder from "../folder";
import UntrackedModel from "../../../../common/models/untracked/untrackedModel";
import Form from "../form";
import License from "../license";
import store from "../../../../common/state/store";
import Certificate from "../certificate";
import ObjectModel from "../objectModel";
import ListView from "../../../../common/models/listViews/listView";
import DetailView from "../../../../common/models/detailView/detailView";
import {
  FolderDocumentFindKeyMap,
  FolderFindKeyMap,
  TFolderDocument,
  TFolderFindKey
} from "~/interfaces/organizationDocumentInterfaces";
import Mapping from "../../../../common/models/mapping/mapping";
import PublicProfile from "../../../../common/models/publicProfile/publicProfile";
import ProcessFlow from "~/models/processFlows/processFlow";
import {Logger} from "~/services/logger";

export default class Folderable extends UntrackedModel {
  folders: Folder[] = [];
  forms: Form[] = [];
  licenses: License[] = [];
  certificates: Certificate[] = [];
  object_models: ObjectModel[] = [];
  list_views: ListView[] = [];
  detail_views: DetailView[] = [];
  mappings: Mapping[] = [];
  public_profiles: PublicProfile[] = [];
  process_flows: ProcessFlow[] = [];
  // organization = null;

  find(id: string): TFolderDocument {
    return this.findDocument(id) || this.findFolder(id);
  }

  get organization() {
    // defined in inherited models
    return null;
  }

  set organization(org) {
    // defined in inherited models
  }

  hasFolders(): boolean {
    return this.folders.length > 0;
  }

  hasForms(): boolean {
    return null;
  }

  hasLicenses(): boolean {
    return null;
  }

  hasCertificates(): boolean {
    return null;
  }

  hasObjectModels(): boolean {
    return null;
  }

  hasListViews(): boolean {
    return null;
  }

  hasDetailViews(): boolean {
    return null;
  }

  hasMappings(): boolean {
    return null;
  }

  hasPublicProfiles(): boolean {
    return null;
  }

  hasProcessFlows(): boolean {
    return null;
  }

  hasDocuments(): boolean {
    return Object.values(FolderDocumentFindKeyMap).some(v => this[`has${v}s`]())
  }

  hasContent(): boolean {
    return this.hasFolders() || this.hasDocuments();
  }

  eachDescendant(callback): void {
    for (let i = 0, len = this.folders.length; i < len; i++) {
      const descendant = this.folders[i];
      callback(descendant);
      descendant.eachDescendant(callback);
    }
  }

  get descendants(): Folder[] {
    const folders = [];
    this.eachDescendant((descendant) => folders.push(descendant));
    return folders;
  }

  findDocument(id: string): TFolderDocument {
    for (let k in FolderDocumentFindKeyMap) {
      const found = this[`find${FolderDocumentFindKeyMap[k]}`](id)
      if (found) return found
    }
    return null
  }

  findForm(id): Form {
    return this.documentFinder<Form>("forms")(id)
  }

  findLicense(id): License {
    return this.documentFinder<License>("licenses")(id)
  }

  findCertificate(id): Certificate {
    return this.documentFinder<Certificate>("certificates")(id)
  }

  findObjectModel(id): ObjectModel {
    return this.documentFinder<ObjectModel>("object_models")(id)
  }

  findListView(id): ListView {
    return this.documentFinder<ListView>("list_views")(id)
  }

  findFolder(id): Folder {
    return this.documentFinder<Folder>("folders")(id)
  }

  findDetailView(id): DetailView {
    return this.documentFinder<DetailView>("detail_views")(id)
  }

  findMapping(id): Mapping {
    return this.documentFinder<Mapping>("mappings")(id)
  }

  findPublicProfile(id): PublicProfile {
    return this.documentFinder<PublicProfile>("public_profiles")(id)
  }

  findProcessFlow(id): ProcessFlow {
    return this.documentFinder<ProcessFlow>("process_flows")(id)
  }

  moveFolder(folder, newParent): void {
    const oldParent = folder.parent;
    // don't move if trying to move to:
    // current location, itself, a child folder
    if (oldParent !== newParent && folder !== newParent && !folder.findFolder(newParent.id)) {
      store.dispatch('organizationStore/moveFolder', {
        organization: this.organization,
        folder: folder,
        newParent: newParent
      });
      folder.parent.removeFolder(folder);
      newParent.addFolder(folder);
      newParent.expanded = true;
      if (!oldParent.hasFolders()) {
        oldParent.expanded = false;
      }
    }
  }

  addFolder(folder): void {
    this.folders.push(folder);
    folder.parent = this;
  }

  removeFolder(folder): void {
    const index = this.folders.indexOf(folder);
    if (index > -1) {
      this.folders.splice(index, 1);
      folder.parent = null;
    }
  }

  openFolders(...folderIds: string[]) {
    folderIds.forEach(folderId => {
      this.findFolder(folderId).open();
    });
  }

  destroyFolder(folder): void {
    store.dispatch('organizationStore/destroyFolder', {organization: this, folder: folder});
  }

  documentFinder<T extends TFolderDocument>(documentKey: TFolderFindKey): (id: string) => T {
    const key = FolderFindKeyMap[documentKey]
    if (!key) {
      Logger.errorOnce('Folderable', `documentFinder cannot find key for ${documentKey} in order to find Folderable document.`)
    }
    return (id: string) => {
      let found =  (<Array<T>>this[documentKey]).find((doc) => doc.id === id);

      if (found) {
        return found;
      } else {
        for (let i = 0, len = this.folders.length; i < len; i++) {
          found = (<Folder>this.folders[i])[`find${key}`](id);
          if (found) {
            return found;
          }
        }
      }
    }
  }
}
